import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Gift, Copy, CheckCircle, Users, Loader2 } from "lucide-react";

function ReferralCode({ code, role }) {
  const [copied, setCopied] = useState(false);

  const copyCode = () => {
    if (code) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="mb-4">
      <p className="text-xs text-[#64748B] mb-1.5">Votre code de parrainage</p>
      <div className="flex items-center gap-2">
        <div className="flex-1 bg-[#F8FAFC] border border-[#CBD5E1] rounded-lg px-3 py-2 font-mono text-sm font-bold text-[#0A2540] tracking-wider">
          {code}
        </div>
        <Button size="sm" variant="outline" onClick={copyCode} className="border-[#CBD5E1] h-9" data-testid="copy-referral-code">
          {copied ? <CheckCircle className="w-4 h-4 text-[#10B981]" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>
      <p className="text-[10px] text-[#64748B] mt-1">
        Partagez ce code. {role === "driver" ? "Vous recevrez 250 TFT" : "Votre filleul recevra 3% de reduction"} apres sa premiere reservation.
      </p>
    </div>
  );
}

function ReferralStats({ info }) {
  return (
    <div className="grid grid-cols-2 gap-3 mb-4">
      <div className="bg-[#F8FAFC] rounded-lg p-3 border border-[#CBD5E1]">
        <p className="text-xs text-[#64748B]">Filleuls</p>
        <p className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>
          <Users className="w-4 h-4 inline mr-1 text-[#64748B]" />
          {info?.total_referrals || 0}
        </p>
      </div>
      <div className="bg-[#F8FAFC] rounded-lg p-3 border border-[#CBD5E1]">
        <p className="text-xs text-[#64748B]">Actifs</p>
        <p className="text-lg font-bold text-[#10B981]" style={{ fontFamily: 'Outfit' }}>
          <CheckCircle className="w-4 h-4 inline mr-1" />
          {info?.completed_referrals || 0}
        </p>
      </div>
    </div>
  );
}

function ApplyReferralCode({ onApplied }) {
  const [code, setCode] = useState("");
  const [applying, setApplying] = useState(false);
  const [msg, setMsg] = useState("");

  const applyCode = async () => {
    if (!code.trim()) return;
    setApplying(true);
    setMsg("");
    try {
      const { data } = await api.post("/referral/apply", { referral_code: code.trim() });
      setMsg(data.message);
      setCode("");
      onApplied();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Erreur");
    } finally { setApplying(false); }
  };

  return (
    <div className="border-t border-[#CBD5E1] pt-4">
      <p className="text-xs text-[#64748B] mb-1.5">Vous avez un code de parrainage ?</p>
      <div className="flex gap-2">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Entrez le code"
          className="h-9 border-[#CBD5E1] font-mono text-sm flex-1"
          data-testid="apply-referral-input"
        />
        <Button size="sm" onClick={applyCode} disabled={applying || !code.trim()} className="bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308] font-semibold h-9" data-testid="apply-referral-btn">
          {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Appliquer"}
        </Button>
      </div>
      {msg && <p className={`text-xs mt-1.5 ${msg.includes("Erreur") || msg.includes("invalide") ? "text-[#EF4444]" : "text-[#10B981]"}`}>{msg}</p>}
    </div>
  );
}

export default function ReferralSection({ user }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchInfo = useCallback(async () => {
    try {
      const { data } = await api.get("/referral/my-info");
      setInfo(data);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.warn("Referral info error:", e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  if (loading) return null;

  return (
    <div className="bg-white rounded-xl border border-[#CBD5E1] p-5" data-testid="referral-section">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-[#FACC15]/20 flex items-center justify-center">
          <Gift className="w-4 h-4 text-[#EAB308]" />
        </div>
        <h3 className="text-base font-semibold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Parrainage</h3>
      </div>

      {info?.referral_code && <ReferralCode code={info.referral_code} role={user?.role} />}
      <ReferralStats info={info} />

      {!info?.referred_by && <ApplyReferralCode onApplied={fetchInfo} />}

      {info?.referred_by && (
        <div className="border-t border-[#CBD5E1] pt-3">
          <p className="text-xs text-[#10B981] flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Parraine avec le code: {info.referred_by}
          </p>
        </div>
      )}
    </div>
  );
}
